import { Suspense, lazy } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { RequireAuth } from './auth/RequireAuth';
import { Spinner } from './components/ui';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { ChannelPage } from './pages/ChannelPage';
import { UploadPage } from './pages/UploadPage';

/*
 * The watch page pulls in hls.js, which is ~590KB minified -- more than twice
 * the rest of the app. Loading it on the home page, where nothing plays, would
 * make the first paint of every visit pay for a library most visits never use.
 */
const WatchPage = lazy(async () => ({ default: (await import('./pages/WatchPage')).WatchPage }));
import { LoginPage, SignupPage } from './pages/AuthPages';
import { SettingsPage } from './pages/SettingsPage';
import { StudioPage, VideoEditPage } from './pages/StudioPage';
import { HistoryPage } from './pages/HistoryPage';

const NotFoundPage = (): JSX.Element => (
  <div className="mx-auto max-w-md py-16 text-center">
    <h1 className="text-2xl font-semibold">Not found</h1>
    <p className="mt-2 text-sm text-ink-300">That page does not exist.</p>
    <Link to="/" className="mt-4 inline-block text-brand-400 hover:underline">
      Back to home
    </Link>
  </div>
);

export const App = (): JSX.Element => (
  <Routes>
    <Route element={<AppShell />}>
      <Route index element={<HomePage />} />
      <Route path="search" element={<SearchPage />} />
      {/* Channels are addressed the way they are written: /@handle */}
      <Route path="@:handle" element={<ChannelPage />} />
      <Route
        path="watch/:videoId"
        element={
          <Suspense fallback={<Spinner label="Loading player" />}>
            <WatchPage />
          </Suspense>
        }
      />
      <Route path="login" element={<LoginPage />} />
      <Route path="signup" element={<SignupPage />} />
      <Route
        path="upload"
        element={
          <RequireAuth>
            <UploadPage />
          </RequireAuth>
        }
      />
      <Route
        path="studio"
        element={
          <RequireAuth>
            <StudioPage />
          </RequireAuth>
        }
      />
      <Route
        path="studio/:videoId"
        element={
          <RequireAuth>
            <VideoEditPage />
          </RequireAuth>
        }
      />
      <Route
        path="settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="history"
        element={
          <RequireAuth>
            <HistoryPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
);
